// Ontwikkelaarsmodus: toont testgereedschap (zoals de weersimulator) dat gewone gebruikers niet nodig hebben.
// Aanzetten: 5 keer snel tikken op het versienummer onderaan het menu. Opslag: KEYS.devMode ('1' of leeg).
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { KEYS } from '../storage/keys';
import { getFlag, removeKey, setRaw } from '../storage/localStore';

export interface DevModeApi {
  devMode: boolean;
  /** Registreer een tik op het versienummer. Retourneert true als de modus hierdoor is omgeschakeld. */
  registerTap(): boolean;
  setDevMode(on: boolean): void;
}

const EVENT = 'gameon:devmode';

function read(): boolean {
  try {
    return getFlag(KEYS.devMode);
  } catch {
    return false;
  }
}

function subscribe(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === KEYS.devMode) onChange();
  };
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

function write(on: boolean): void {
  if (on) setRaw(KEYS.devMode, '1');
  else removeKey(KEYS.devMode);
  window.dispatchEvent(new Event(EVENT));
}

/** Hook; werkt tussen meerdere componenten en tabbladen synchroon (storage-event + eigen event). */
export function useDevMode(): DevModeApi {
  const devMode = useSyncExternalStore(subscribe, read, () => false);
  const taps = useRef<number[]>([]);

  const setDevMode = useCallback((on: boolean) => write(on), []);

  const registerTap = useCallback((): boolean => {
    const now = Date.now();
    const prior = taps.current.filter((t) => now - t <= 3000);
    if (tapsReachThreshold(prior, now)) {
      taps.current = [];
      write(!read());
      return true;
    }
    taps.current = [...prior, now];
    return false;
  }, []);

  // Tikken vergeten bij het verlaten van het component.
  useEffect(() => () => {
    taps.current = [];
  }, []);

  return { devMode, registerTap, setDevMode };
}

/** Pure teller: 5 tikken binnen 3 seconden. `taps` zijn tijdstippen (ms) van EERDERE tikken; de tik op `now` telt mee. */
export function tapsReachThreshold(taps: readonly number[], now: number, needed = 5, windowMs = 3000): boolean {
  // De tik van nu telt mee.
  const count = taps.filter((t) => now - t <= windowMs && t <= now).length + 1;
  return count >= needed;
}
